import {useCallback, useEffect, useRef, useState} from "react";
import {FOOTER_CONTACTS} from "./utils";
import {
    TRAINING_GALLERY,
    TRAINING_NAV,
    TRAINING_PROMISES,
    TRAINING_SECTORS,
    TRAINING_SOLUTIONS,
    TRAINING_START_STEPS,
    TRAINING_STEPS,
} from "./trainingContent";

const MAIL = "mailto:info@onenergygroups.com?subject=Training%20enquiry";

const TrainingGallery = () => {
    const trackRef = useRef<HTMLDivElement>(null);
    const [page, setPage] = useState(0);
    const [pageCount, setPageCount] = useState(1);

    const metrics = useCallback(() => {
        const track = trackRef.current;
        const card = track?.querySelector("figure");
        if (!track || !(card instanceof HTMLElement)) {
            return null;
        }
        const styles = getComputedStyle(track);
        const gap = Number.parseFloat(styles.columnGap || styles.gap) || 16;
        const cardWidth = card.getBoundingClientRect().width + gap;
        const visible = Math.max(1, Math.round(track.clientWidth / cardWidth));
        return {track, cardWidth, visible, pages: Math.max(1, Math.ceil(TRAINING_GALLERY.length / visible))};
    }, []);

    const syncPage = useCallback(() => {
        const next = metrics();
        if (!next) {
            return;
        }
        const {track, cardWidth, visible, pages} = next;
        const index = Math.round(track.scrollLeft / (cardWidth * visible));
        setPageCount(pages);
        setPage(Math.min(Math.max(0, index), pages - 1));
    }, [metrics]);

    useEffect(() => {
        syncPage();
        window.addEventListener("resize", syncPage);
        return () => window.removeEventListener("resize", syncPage);
    }, [syncPage]);

    const goTo = (nextPage: number) => {
        const next = metrics();
        if (!next) {
            return;
        }
        const clamped = Math.min(Math.max(0, nextPage), next.pages - 1);
        next.track.scrollTo({left: clamped * next.cardWidth * next.visible, behavior: "smooth"});
    };

    return (
        <div className="oec-training-gallery">
            <div
                ref={trackRef}
                className="oec-training-gallery-track"
                onScroll={syncPage}
                tabIndex={0}
                aria-label="Training session photos"
            >
                {TRAINING_GALLERY.map((photo) => (
                    <figure key={photo.src} className="oec-training-gallery-item">
                        <img src={photo.src} alt={photo.alt} loading="lazy"/>
                        <figcaption>{photo.alt}</figcaption>
                    </figure>
                ))}
            </div>
            <div className="oec-training-gallery-controls">
                <button
                    type="button"
                    className="oec-training-gallery-arrow"
                    aria-label="Previous photos"
                    disabled={page <= 0}
                    onClick={() => goTo(page - 1)}
                >
                    <i className="fas fa-chevron-left" aria-hidden="true"/>
                </button>
                <div className="oec-training-gallery-dots" role="tablist" aria-label="Gallery pages">
                    {Array.from({length: pageCount}, (_, index) => (
                        <button
                            key={index}
                            type="button"
                            className={index === page ? "is-active" : undefined}
                            aria-label={`Show photos ${index + 1}`}
                            aria-current={index === page ? "true" : undefined}
                            onClick={() => goTo(index)}
                        />
                    ))}
                </div>
                <button
                    type="button"
                    className="oec-training-gallery-arrow"
                    aria-label="Next photos"
                    disabled={page >= pageCount - 1}
                    onClick={() => goTo(page + 1)}
                >
                    <i className="fas fa-chevron-right" aria-hidden="true"/>
                </button>
            </div>
        </div>
    );
};

export const TrainingPage = () => {
    return (
        <article className="oec-training">
            <section className="oec-training-hero">
                <img
                    className="oec-training-hero-photo"
                    src="/public/training/classroom.jpg"
                    alt="Participants in an On Energy training session"
                    fetchPriority="high"
                    decoding="async"
                />
                <div className="oec-training-hero-shade"/>
                <div className="oec-training-hero-inner">
                    <p className="oec-training-kicker">Training &amp; capacity development</p>
                    <h1>Practical learning for teams that deliver energy and development programmes.</h1>
                    <p className="oec-training-lead">
                        On Energy Consult designs and delivers training that strengthens staff capability,
                        improves workplace performance and turns learning into action — for organisations
                        in Ethiopia, across Africa and with international partners.
                    </p>
                    <div className="oec-training-hero-actions">
                        <a className="btn-oec-primary" href="#engage">Request a programme</a>
                        <a className="btn-oec-secondary oec-training-hero-secondary" href="#solutions">View solutions</a>
                    </div>
                </div>
            </section>

            <nav className="oec-training-nav" aria-label="On this page">
                <div className="oec-section-inner oec-training-nav-row">
                    {TRAINING_NAV.map((item) => (
                        <a key={item.href} href={item.href}>{item.label}</a>
                    ))}
                </div>
            </nav>

            <section className="oec-section oec-training-block" id="who-we-are">
                <div className="oec-section-inner">
                    <div className="oec-training-split">
                        <div>
                            <p className="oec-training-kicker oec-training-kicker-dark">Who we are</p>
                            <h2 className="oec-section-title">Practical learning. Stronger teams. Better delivery.</h2>
                            <p className="oec-training-copy">
                                On Energy Consult &amp; Trading PLC is an Ethiopian consultancy established in 2017,
                                working across energy, sustainability, research, training and development consulting.
                                Our Training &amp; Capacity Development work helps organisations strengthen staff
                                capabilities and translate learning into day-to-day performance.
                            </p>
                            <p className="oec-training-copy">
                                Energy-sector experience is a specialist strength. We also support leadership,
                                professional skills, project delivery, organisational capacity, business development
                                and monitoring, evaluation and learning.
                            </p>
                        </div>
                        <figure className="oec-training-figure">
                            <img src="/public/training/session-listen.jpg" alt="On Energy facilitator leading a session" loading="lazy"/>
                        </figure>
                    </div>
                    <ul className="oec-training-promise-grid">
                        {TRAINING_PROMISES.map((promise) => (
                            <li key={promise.title}>
                                <h3>{promise.title}</h3>
                                <p>{promise.text}</p>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section className="oec-section oec-section-tint oec-training-block" id="solutions">
                <div className="oec-section-inner">
                    <p className="oec-training-kicker oec-training-kicker-dark">Our training solutions</p>
                    <h2 className="oec-section-title">Open courses, in-house programmes and facilitated workshops.</h2>
                    <p className="oec-training-copy oec-training-copy-wide">
                        Content and duration are agreed with each client. We offer open-enrolment courses,
                        tailored in-house training, facilitated workshops and programme-based capacity development.
                    </p>
                    <div className="oec-training-solution-grid">
                        {TRAINING_SOLUTIONS.map((solution) => (
                            <article key={solution.number} className="oec-training-card">
                                <span className="oec-training-number">{solution.number}</span>
                                <h3>{solution.title}</h3>
                                <p className="oec-training-audience">{solution.audience}</p>
                                <ul>
                                    {solution.items.map((item) => (
                                        <li key={item}>{item}</li>
                                    ))}
                                </ul>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="oec-section oec-training-block" id="sectors">
                <div className="oec-section-inner">
                    <div className="oec-training-split">
                        <figure className="oec-training-figure">
                            <img src="/public/training/workshop.jpg" alt="Participants in a hands-on group exercise" loading="lazy"/>
                        </figure>
                        <div>
                            <p className="oec-training-kicker oec-training-kicker-dark">Sector-specific learning</p>
                            <h2 className="oec-section-title">Built around your sector, not a generic curriculum.</h2>
                            <p className="oec-training-copy">
                                We combine professional and commercial capabilities with sector context, and work
                                with qualified technical experts where a course requires specialist instruction,
                                equipment or formal certification.
                            </p>
                            <p className="oec-training-copy">
                                Before delivery we agree participant profile, learning outcomes, course level,
                                examples, format, assessment method and expected outputs.
                            </p>
                        </div>
                    </div>
                    <div className="oec-training-sector-grid">
                        {TRAINING_SECTORS.map((sector) => (
                            <article key={sector.title} className="oec-training-sector">
                                <h3>{sector.title}</h3>
                                <p>{sector.text}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="oec-section oec-section-tint oec-training-block" id="how-we-work">
                <div className="oec-section-inner">
                    <p className="oec-training-kicker oec-training-kicker-dark">How we work</p>
                    <h2 className="oec-section-title">A structured process that is relevant, measurable and usable.</h2>
                    <ol className="oec-training-steps">
                        {TRAINING_STEPS.map((step) => (
                            <li key={step.number}>
                                <span>{step.number}</span>
                                <h3>{step.title}</h3>
                                <p>{step.text}</p>
                            </li>
                        ))}
                    </ol>
                    <p className="oec-training-copy oec-training-copy-wide">
                        Delivery can be an open-enrolment course, an in-house programme, a facilitated workshop,
                        a blended session or a programme with local or technical partners. Duration, location
                        and language are agreed with the client.
                    </p>
                    <figure className="oec-training-wide-photo">
                        <img src="/public/training/cohort.jpg" alt="On Energy training cohort outdoors" loading="lazy"/>
                    </figure>
                </div>
            </section>

            <section className="oec-section oec-training-block" id="gallery">
                <div className="oec-section-inner">
                    <p className="oec-training-kicker oec-training-kicker-dark">In the room</p>
                    <h2 className="oec-section-title">Where people learn, practise and leave with next steps.</h2>
                    <p className="oec-training-copy oec-training-copy-wide">
                        Real sessions: group work, facilitated discussion, classroom delivery and completion —
                        not stock photography.
                    </p>
                    <TrainingGallery />
                </div>
            </section>

            <section className="oec-training-engage" id="engage">
                <div className="oec-section-inner oec-training-engage-inner">
                    <div>
                        <p className="oec-training-kicker">Engage On Energy</p>
                        <h2>Let’s build capability that translates into performance.</h2>
                        <p>
                            Whether you need a focused workshop, a tailored staff-development programme or a
                            delivery partner for a wider capacity-building initiative, we can define the
                            learning need and develop a practical response.
                        </p>
                        <ol className="oec-training-start">
                            {TRAINING_START_STEPS.map((step) => (
                                <li key={step}>{step}</li>
                            ))}
                        </ol>
                        <div className="oec-training-hero-actions">
                            <a className="btn-oec-primary" href={MAIL}>Email a training enquiry</a>
                            <a className="btn-oec-secondary oec-training-hero-secondary" href="/about#contact">Use the contact form</a>
                        </div>
                        <ul className="oec-training-contacts">
                            {FOOTER_CONTACTS.map((item) => (
                                <li key={item.value}>
                                    <i className={item.icon}/>
                                    <a href={item.href}>{item.value}</a>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <figure className="oec-training-figure oec-training-figure-dark">
                        <img src="/public/training/team.jpg" alt="On Energy training team" loading="lazy"/>
                    </figure>
                </div>
            </section>
        </article>
    );
};
